import { useEffect, useState } from "react";
import { NavLink, Outlet, useNavigate, useLocation, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  LayoutDashboard, Users, FileText, CalendarClock, Activity, Building2,
  Settings, Search, LogOut, Menu, ChevronDown, ClipboardCheck, CreditCard, Lock, Sparkles, HelpCircle, HardHat, LifeBuoy, UserCog, MapPin, UserCheck, FileCheck, Boxes, Plane, HeartPulse,
} from "lucide-react";
import { Clock } from "lucide-react";
import { Newspaper } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator, DropdownMenuLabel } from "@/components/ui/dropdown-menu";
import { Logo } from "@/components/Logo";
import { NotificationBell } from "@/components/NotificationBell";
import { LanguageSwitcher } from "@/components/LanguageSwitcher";
import { useAuth } from "@/context/AuthContext";
import { TourGuide } from "@/components/TourGuide";
import api from "@/lib/api";

function GlobalSearch() {
  const { t } = useTranslation();
  const [q, setQ] = useState("");
  const [res, setRes] = useState(null);
  const nav = useNavigate();
  useEffect(() => {
    if (!q || q.length < 2) { setRes(null); return; }
    const timer = setTimeout(async () => {
      try { const { data } = await api.get(`/employer/search?q=${encodeURIComponent(q)}`); setRes(data); } catch {}
    }, 250);
    return () => clearTimeout(timer);
  }, [q]);
  return (
    <div className="relative hidden max-w-md flex-1 md:block">
      <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
      <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder={t("empNav.search")} data-testid="global-search" className="pl-9" />
      {res && (
        <div className="absolute z-50 mt-2 w-full rounded-xl border border-slate-200 bg-white p-2 shadow-lg" data-testid="search-results">
          {res.employees.length === 0 && res.documents.length === 0 && res.deadlines.length === 0 && <div className="px-3 py-4 text-sm text-slate-400">—</div>}
          {res.employees.map((e) => (
            <button key={e.id} onMouseDown={() => { setQ(""); setRes(null); nav(`/app/employer/employees/${e.id}`); }} className="flex w-full items-center gap-2 rounded-lg px-3 py-2 text-left text-sm hover:bg-slate-50">
              <Users className="h-4 w-4 text-cyan-dark" /> {e.first_name} {e.last_name}
            </button>
          ))}
          {res.documents.map((d) => <div key={d.id} className="flex items-center gap-2 rounded-lg px-3 py-2 text-sm text-slate-600"><FileText className="h-4 w-4 text-slate-400" /> {d.filename}</div>)}
          {res.deadlines.map((d) => <div key={d.id} className="flex items-center gap-2 rounded-lg px-3 py-2 text-sm text-slate-600"><CalendarClock className="h-4 w-4 text-slate-400" /> {d.title}</div>)}
        </div>
      )}
    </div>
  );
}

export default function EmployerLayout() {
  const { t } = useTranslation();
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const [open, setOpen] = useState(false);
  const [plan, setPlan] = useState(null);
  const [modAccess, setModAccess] = useState(null);
  const [timeAccess, setTimeAccess] = useState(null);
  const [careAccess, setCareAccess] = useState(null);
  const [logoUrl, setLogoUrl] = useState(null);
  const [newsUnseen, setNewsUnseen] = useState(0);
  const [corrOpen, setCorrOpen] = useState(0);
  const [pcrOpen, setPcrOpen] = useState(0);
  const [docvOpen, setDocvOpen] = useState(0);
  const company = user?.organization?.name || "";
  const _ind = (user?.organization?.industry || "").toUpperCase();
  const isCare = _ind === "CARE";
  const showCare = isCare && (careAccess === "full" || careAccess === "read_only");

  useEffect(() => {
    const check = () => {
      const seen = localStorage.getItem("eg_news_seen");
      api.get(`/employer/compliance-news/unseen${seen ? `?since=${encodeURIComponent(seen)}` : ""}`)
        .then((r) => {
          setNewsUnseen(r.data.unseen || 0);
          if (!seen && r.data.latest) localStorage.setItem("eg_news_seen", r.data.latest);
        })
        .catch(() => {});
    };
    check();
    const onSeen = () => setNewsUnseen(0);
    window.addEventListener("eg-news-seen", onSeen);
    return () => window.removeEventListener("eg-news-seen", onSeen);
  }, []);

  useEffect(() => {
    api.get("/employer/subscription").then((r) => setPlan(r.data.plan)).catch(() => {});
  }, [loc.pathname]);
  useEffect(() => {
    let revoked = null;
    api.get("/employer/company/logo", { responseType: "blob" })
      .then((r) => { const u = URL.createObjectURL(r.data); revoked = u; setLogoUrl(u); })
      .catch(() => setLogoUrl(null));
    return () => { if (revoked) URL.revokeObjectURL(revoked); };
  }, []);
  useEffect(() => {
    api.get("/employer/entitlements").then((r) => {
      setModAccess(r.data.entitlements?.SUBCONTRACTOR_COMPLIANCE?.access || "none");
      setTimeAccess(r.data.entitlements?.TIME_TRACKING?.access || "none");
      setCareAccess(r.data.entitlements?.CARE_MODULE?.access || "none");
    }).catch(() => {});
    api.get("/employer/time/correction-requests?status=PENDING")
      .then((r) => setCorrOpen((r.data.requests || []).length)).catch(() => {});
    api.get("/employer/profile-change-requests?status=PENDING")
      .then((r) => setPcrOpen((r.data.requests || []).length)).catch(() => {});
    api.get("/employer/document-verifications?status=PENDING")
      .then((r) => setDocvOpen((r.data.documents || []).length)).catch(() => {});
  }, [loc.pathname]);
  const onBilling = loc.pathname.startsWith("/app/employer/billing");
  const onOverview = loc.pathname === "/app/employer";
  const preview = !!plan?.preview_mode;
  const locked = preview ? (!onBilling && !onOverview) : (!!plan?.read_only && !onBilling);

  const [tourOpen, setTourOpen] = useState(false);
  const tourSteps = [
    { selector: '[data-testid="global-search"]', title: t("tour.s1t"), body: t("tour.s1b") },
    { selector: '[data-testid="nav-employees"]', title: t("tour.s2t"), body: t("tour.s2b") },
    { selector: '[data-testid="nav-documents"]', title: t("tour.s3t"), body: t("tour.s3b") },
    { selector: '[data-testid="nav-renewals"]', title: t("tour.s4t"), body: t("tour.s4b") },
    { selector: '[data-testid="nav-billing"]', title: t("tour.s5t"), body: t("tour.s5b") },
    { selector: '[data-testid="trial-banner"]', title: t("tour.s6t"), body: t("tour.s6b") },
  ];
  const endTour = () => { localStorage.setItem("eg_tour_done", "1"); setTourOpen(false); };
  useEffect(() => {
    const h = () => setTourOpen(true);
    window.addEventListener("eg-start-tour", h);
    return () => window.removeEventListener("eg-start-tour", h);
  }, []);
  useEffect(() => {
    if (locked) return;
    if (localStorage.getItem("eg_onboarded") && !localStorage.getItem("eg_tour_done")) {
      const id = setTimeout(() => { if (!document.querySelector('[data-testid="onboarding-modal"]')) setTourOpen(true); }, 1200);
      return () => clearTimeout(id);
    }
  }, [locked]);

  const links = [
    { to: "/app/employer", label: t("empNav.overview"), icon: LayoutDashboard, end: true, group: "core" },
    { to: "/app/employer/employees", label: t("empNav.employees"), icon: Users, section: "employees", group: "core" },
    { to: "/app/employer/profile-confirmations", label: "Profil-Bestätigungen", icon: UserCheck, section: "employees", group: "core" },
    { to: "/app/employer/document-verifications", label: "Dokument-Prüfung", icon: FileCheck, section: "employees", group: "core" },
    { to: "/app/employer/documents", label: t("empNav.documents"), icon: FileText, section: "documents", group: "core" },
    { to: "/app/employer/deadlines", label: t("empNav.deadlines"), icon: CalendarClock, section: "deadlines", group: "core" },
    { to: "/app/employer/renewals", label: t("guide.navRenewals"), icon: ClipboardCheck, section: "renewals", group: "core" },
    { to: "/app/employer/compliance-news", label: t("ai.newsNav"), icon: Newspaper, section: "compliance-news", group: "core" },
    { to: "/app/employer/time", label: t("time.nav"), icon: Clock, moduleKey: "TIME_TRACKING", section: "time", group: "modules" },
    { to: "/app/employer/time/corrections", label: "Korrekturanfragen", icon: ClipboardCheck, section: "time", group: "modules" },
    { to: "/app/employer/baustellen", label: t("bau.nav"), icon: MapPin, group: "modules", constructionOnly: true },
    { to: "/app/employer/immigration", label: t("im.nav"), icon: Plane, group: "modules" },
    { to: "/app/employer/contracts", label: "Arbeitsverträge", icon: FileText, group: "modules" },
    { to: "/app/employer/modules", label: "Module", icon: Boxes, group: "account" },
    { to: "/app/employer/billing", label: t("bill.navBilling"), icon: CreditCard, section: "billing", group: "account" },
    { to: "/app/employer/activity", label: t("empNav.activity"), icon: Activity, section: "activity", group: "account" },
    { to: "/app/employer/company", label: t("empNav.company"), icon: Building2, section: "company", group: "account" },
    { to: "/app/employer/team", label: t("tm.nav"), icon: UserCog, group: "account" },
    { to: "/app/employer/settings", label: t("empNav.settings"), icon: Settings, group: "account" },
    { to: "/app/employer/support", label: t("sup.nav"), icon: LifeBuoy, group: "account" },
  ];

  const secAccess = user?.role === "COMPANY_ADMIN" && Array.isArray(user?.section_access) ? user.section_access : null;
  const visibleLinks = links.filter((l) => (!secAccess || !l.section || secAccess.includes(l.section)) && !(isCare && l.constructionOnly));

  const renderLink = (l) => (
    <NavLink key={l.to} to={l.to} end={l.end} onClick={() => setOpen(false)} data-testid={`nav-${l.to.split("/").pop() || "overview"}`}
      className={({ isActive }) => `flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-medium transition-colors ${isActive ? "bg-cyan/10 text-cyan-dark" : "text-slate-600 hover:bg-slate-100"}`}>
      <l.icon className="h-4.5 w-4.5" strokeWidth={1.75} /> <span className="flex-1">{l.label}</span>
      {l.to === "/app/employer/compliance-news" && newsUnseen > 0 && (
        <span className="flex h-5 min-w-[20px] items-center justify-center rounded-full bg-red-500 px-1.5 text-[10px] font-bold text-white" data-testid="news-unseen-badge">{newsUnseen > 9 ? "9+" : newsUnseen}</span>
      )}
      {l.to === "/app/employer/time/corrections" && corrOpen > 0 && (
        <span className="flex h-5 min-w-[20px] items-center justify-center rounded-full bg-amber-500 px-1.5 text-[10px] font-bold text-white" data-testid="corr-open-badge">{corrOpen > 9 ? "9+" : corrOpen}</span>
      )}
      {l.to === "/app/employer/profile-confirmations" && pcrOpen > 0 && (
        <span className="flex h-5 min-w-[20px] items-center justify-center rounded-full bg-amber-500 px-1.5 text-[10px] font-bold text-white" data-testid="pcr-open-badge">{pcrOpen > 9 ? "9+" : pcrOpen}</span>
      )}
      {l.to === "/app/employer/document-verifications" && docvOpen > 0 && (
        <span className="flex h-5 min-w-[20px] items-center justify-center rounded-full bg-amber-500 px-1.5 text-[10px] font-bold text-white" data-testid="docv-open-badge">{docvOpen > 9 ? "9+" : docvOpen}</span>
      )}
      {l.moduleKey === "TIME_TRACKING" && timeAccess !== "full" && timeAccess !== "read_only" && (
        <span className="rounded-full bg-cyan/15 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider text-cyan-dark" data-testid="time-pro-badge">{t("sub.pro")}</span>
      )}
    </NavLink>
  );

  const GroupLabel = ({ children }) => (
    <div className="px-3 pb-1 pt-3 text-[10px] font-bold uppercase tracking-wider text-slate-400">{children}</div>
  );

  const Sidebar = () => (
    <div className="flex h-full flex-col">
      <div className="flex h-16 items-center border-b border-slate-200 px-5"><Link to="/app/employer"><Logo /></Link></div>
      <nav className="min-h-0 flex-1 space-y-0.5 overflow-y-auto p-3">
        {preview ? (
          links.filter((l) => l.to === "/app/employer" || l.to === "/app/employer/billing").map(renderLink)
        ) : (
          <>
            {visibleLinks.filter((l) => l.group === "core").map(renderLink)}

            {(visibleLinks.some((l) => l.group === "modules") || modAccess === "full" || modAccess === "read_only" || showCare) && (
              <GroupLabel>Module</GroupLabel>
            )}
            {visibleLinks.filter((l) => l.group === "modules").map(renderLink)}
            {!isCare && (
            <NavLink to="/app/employer/subcontractors" onClick={() => setOpen(false)} data-testid="nav-subcontractors"
              className={({ isActive }) => `flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-medium transition-colors ${isActive ? "bg-cyan/10 text-cyan-dark" : "text-slate-600 hover:bg-slate-100"}`}>
              <HardHat className="h-4.5 w-4.5" strokeWidth={1.75} /> <span className="flex-1">{t("sub.navLabel")}</span>
              {modAccess !== "full" && modAccess !== "read_only" && (
                <span className="rounded-full bg-cyan/15 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider text-cyan-dark" data-testid="sub-pro-badge">{t("sub.pro")}</span>
              )}
            </NavLink>
            )}
            {showCare && (
              <>
                <NavLink to="/app/employer/pflege" end onClick={() => setOpen(false)} data-testid="nav-pflege"
                  className={({ isActive }) => `flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-medium transition-colors ${isActive ? "bg-cyan/10 text-cyan-dark" : "text-slate-600 hover:bg-slate-100"}`}>
                  <HeartPulse className="h-4.5 w-4.5" strokeWidth={1.75} /> <span className="flex-1">Pflege</span>
                </NavLink>
                {[["/app/employer/pflege/dispatch", "Disposition", true], ["/app/employer/pflege/tours", "Touren", true], ["/app/employer/pflege/visits", "Besuche", true], ["/app/employer/pflege/patients", "Patienten", true], ["/app/employer/pflege/tags", "Care Tags", true], ["/app/employer/pflege/reports", "Berichte", true]].map(([to, label]) => (
                  <NavLink key={to} to={to} onClick={() => setOpen(false)} data-testid={`nav-${to.split("/").pop()}`}
                    className={({ isActive }) => `flex items-center gap-3 rounded-xl py-2 pl-11 pr-3 text-sm font-medium transition-colors ${isActive ? "bg-cyan/10 text-cyan-dark" : "text-slate-500 hover:bg-slate-100"}`}>
                    <span className="flex-1">{label}</span>
                  </NavLink>
                ))}
              </>
            )}

            {visibleLinks.some((l) => l.group === "account") && <div className="my-2 border-t border-slate-100" />}
            {visibleLinks.filter((l) => l.group === "account").map(renderLink)}
          </>
        )}
      </nav>
      <div className="border-t border-slate-200 p-3">
        <div className="rounded-xl bg-slate-50 p-3 text-xs text-slate-500">
          <div className="font-semibold text-navy">{company}</div>
          <div className="mt-0.5">{user?.role?.replace("_", " ")}</div>
        </div>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-background">
      <aside className="fixed inset-y-0 left-0 z-40 hidden w-64 border-r border-slate-200 bg-white lg:block">{Sidebar()}</aside>
      {open && (
        <div className="fixed inset-0 z-50 lg:hidden">
          <div className="absolute inset-0 bg-navy/40" onClick={() => setOpen(false)} />
          <aside className="absolute inset-y-0 left-0 w-64 bg-white">{Sidebar()}</aside>
        </div>
      )}
      <div className="lg:pl-64">
        <header className="glass-nav sticky top-0 z-30 flex h-16 items-center gap-3 border-b border-slate-200 px-4 sm:px-6">
          <button className="lg:hidden" onClick={() => setOpen(true)} data-testid="sidebar-toggle" aria-label="Menu"><Menu className="h-5 w-5 text-navy" /></button>
          <GlobalSearch />
          <div className="ml-auto flex items-center gap-2">
            <button onClick={() => setTourOpen(true)} data-testid="tour-start-btn" title={t("tour.help")} aria-label={t("tour.help")} className="flex h-9 w-9 items-center justify-center rounded-full text-slate-500 transition-colors hover:bg-slate-100 hover:text-navy"><HelpCircle className="h-5 w-5" /></button>
            <LanguageSwitcher />
            <NotificationBell />
            {logoUrl && <img src={logoUrl} alt={company} className="hidden h-8 max-w-[120px] object-contain sm:block" data-testid="header-company-logo" />}
            <span className="hidden text-sm font-medium text-navy sm:block">{company}</span>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button className="flex items-center gap-2 rounded-full border border-slate-200 py-1 pl-1 pr-2 hover:bg-slate-50" data-testid="profile-menu">
                  <span className="flex h-7 w-7 items-center justify-center rounded-full bg-navy text-xs font-bold text-white">{(user?.first_name?.[0] || "") + (user?.last_name?.[0] || "")}</span>
                  <ChevronDown className="h-3.5 w-3.5 text-slate-400" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-52">
                <DropdownMenuLabel>{user?.first_name} {user?.last_name}<div className="text-xs font-normal text-slate-400">{user?.email}</div></DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => nav("/app/employer/settings")}>{t("empNav.settings")}</DropdownMenuItem>
                <DropdownMenuItem onClick={async () => { await logout(); nav("/login"); }} data-testid="logout-btn" className="text-red-600"><LogOut className="mr-2 h-4 w-4" /> {t("empNav.logout")}</DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </header>
        {preview
          ? <PreviewBanner retentionUntil={plan?.retention_until} />
          : (plan?.status === "trialing" && plan?.trial_ends_at && !locked && <TrialBanner endsAt={plan.trial_ends_at} />)}
        <main className="mx-auto max-w-7xl px-4 py-8 sm:px-6">{locked ? <LockScreen /> : <Outlet />}</main>
      </div>
      <TourGuide steps={tourSteps} open={tourOpen} onClose={endTour} />
    </div>
  );
}

function TrialBanner({ endsAt }) {
  const { t } = useTranslation();
  const nav = useNavigate();
  const days = Math.max(0, Math.ceil((new Date(endsAt) - new Date()) / 86400000));
  return (
    <div className="border-b border-cyan/20 bg-cyan/5" data-testid="trial-banner">
      <div className="mx-auto flex max-w-7xl flex-wrap items-center justify-between gap-2 px-4 py-2.5 sm:px-6">
        <div className="flex items-center gap-2 text-sm text-navy"><Sparkles className="h-4 w-4 text-cyan-dark" /><span data-testid="trial-days-left">{t("x.trialBannerDays", { n: days })}</span></div>
        <Button size="sm" onClick={() => nav("/app/employer/billing")} data-testid="trial-banner-cta" className="rounded-full bg-cyan text-white hover:bg-cyan-dark">{t("x.trialContinue")}</Button>
      </div>
    </div>
  );
}

function PreviewBanner({ retentionUntil }) {
  const { t } = useTranslation();
  const nav = useNavigate();
  const days = retentionUntil ? Math.max(0, Math.ceil((new Date(retentionUntil) - new Date()) / 86400000)) : null;
  return (
    <div className="border-b border-amber-200 bg-amber-50" data-testid="preview-banner">
      <div className="mx-auto flex max-w-7xl flex-wrap items-center justify-between gap-2 px-4 py-2.5 sm:px-6">
        <div className="flex items-center gap-2 text-sm text-amber-800">
          <Lock className="h-4 w-4" />
          <span data-testid="preview-banner-text">{t("x.previewBanner")}{days != null ? ` ${t("x.previewKept", { n: days })}` : ""}</span>
        </div>
        <Button size="sm" onClick={() => nav("/app/employer/billing")} data-testid="preview-banner-cta" className="rounded-full bg-navy text-white hover:bg-navy-muted">{t("x.lockedCta")}</Button>
      </div>
    </div>
  );
}

function LockScreen() {
  const { t } = useTranslation();
  const nav = useNavigate();
  return (
    <div className="flex min-h-[60vh] items-center justify-center" data-testid="employer-lock-screen">
      <div className="card-soft max-w-md p-8 text-center">
        <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-2xl bg-amber-50 text-amber-600"><Lock className="h-7 w-7" /></div>
        <h2 className="mt-5 font-heading text-2xl font-extrabold text-navy">{t("x.lockedTitle")}</h2>
        <p className="mt-3 text-sm text-slate-500">{t("x.lockedDesc")}</p>
        <div className="mt-5 space-y-2 rounded-xl bg-slate-50 p-4 text-left text-xs" data-testid="lock-savings">
          <div className="flex justify-between"><span className="text-slate-500">{t("x.savingYearly")}</span><span className="font-bold text-emerald-600">&minus;10%</span></div>
          <div className="flex justify-between"><span className="text-slate-500">{t("x.saving2year")}</span><span className="font-bold text-emerald-600">&minus;30%</span></div>
        </div>
        <Button onClick={() => nav("/app/employer/billing")} data-testid="lock-choose-plan-btn" className="mt-5 w-full rounded-full bg-cyan text-white hover:bg-cyan-dark">{t("x.lockedCta")}</Button>
        <p className="mt-3 text-xs text-slate-400">{t("x.lockedNote")}</p>
      </div>
    </div>
  );
}
